import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight, Building2, TrendingUp, Calendar, ChevronLeft, ChevronRight, Maximize, Sparkles, Users, Factory, LandPlot } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
};

const CountUp = ({ end, duration = 2000, decimals = 0, separator = '.' }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const startTime = performance.now();
          const tick = (now) => {
            const progress = Math.min((now - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(end * eased);
            if (progress < 1) requestAnimationFrame(tick);
            else setValue(end);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [end, duration]);

  const formatted = Number(value)
    .toFixed(decimals)
    .replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  return <span ref={ref}>{formatted}</span>;
};

const counterStats = [
  {
    icon: LandPlot,
    value: 434000,
    suffix: 'm²',
    label: 'Toplam Proje Alanı',
    accent: 'from-blue-500 to-blue-700',
    glow: 'bg-blue-500/30',
  },
  {
    icon: Factory,
    value: 434,
    suffix: '',
    label: 'Birimlik Sanayi İş Yeri',
    accent: 'from-emerald-500 to-emerald-700',
    glow: 'bg-emerald-500/30',
  },
  {
    icon: Users,
    value: 4000,
    suffix: 'm²',
    label: 'Sosyal Tesis Binası',
    accent: 'from-amber-500 to-amber-700',
    glow: 'bg-amber-500/30',
  },
];

const heroSlides = [
  {
    id: 1,
    image: "/images/renders_hero/hero1.jpg",
    subtitle: "GELECEĞİN İŞ MERKEZİ",
    title: "S.S. İkitelli - Silivri \nToplu İşyeri Yapı Kooperatifi",
    desc: "Bölgede iş dünyasının ihtiyaçlarına yönelik modern ve kapsamlı üretim-ticaret alanları oluşturuyoruz."
  },
  {
    id: 3,
    image: "/images/renders_hero/hero3.jpg",
    subtitle: "GÜVENLİ YATIRIM",
    title: "İşletmeniz İçin \nDeğer Yaratan Alanlar",
    desc: "Geniş sosyal donatıları ve modern mimarisiyle iş dünyasının yeni buluşma noktası."
  }
];

const homeNewsItems = [
  {
    id: 1,
    title: 'S.S. İkitelli-Silivri Toplu İşyeri Kooperatifi Temel Atma Töreni',
    date: '12 Ağustos 2024',
    image: '/images/renders/render1.jpg',
    summary: 'Silivri\'de yükselecek olan yeni iş merkezimizin temel atma törenini büyük bir coşku ile gerçekleştirdik. Projemizin ilk aşaması başarıyla başladı.'
  },
  {
    id: 2,
    title: 'Yeni Yönetim Kurulu Toplantısı Gerçekleştirildi',
    date: '05 Eylül 2024',
    image: '/images/renders/render2.jpg',
    summary: 'Proje ilerlemesinin değerlendirildiği aylık yönetim kurulu toplantımızda önemli kararlar alındı ve yatırım planları tartışıldı.'
  },
  {
    id: 3,
    title: 'Altyapı Çalışmalarında 1. Etap Tamamlandı',
    date: '20 Ekim 2024',
    image: '/images/renders/render3.jpg',
    summary: 'Kooperatif arazi alanında başlatılan kazı, su, elektrik ve telekomünikasyon altyapı çalışmaları planlanan tarihten önce bitirildi.'
  },
  {
    id: 4,
    title: 'Silivri Belediye Başkanı\'ndan Proje Alanına Ziyaret',
    date: '15 Kasım 2024',
    image: '/images/renders/render4.jpg',
    summary: 'Bölge protokolü, devam eden projemizi ziyaret ederek çalışmalar hakkında bilgi aldı ve desteklerini iletti.'
  },
  {
    id: 5,
    title: 'İş Güvenliği Eğitimlerimiz Başladı',
    date: '02 Aralık 2024',
    image: '/images/renders/render5.jpg',
    summary: 'Şantiyemizde sıfır kaza hedefiyle, tüm saha personeline yönelik kapsamlı İSG eğitimleri verilmeye başlandı.'
  },
  {
    id: 6,
    title: '2025 Yılı Yatırım Planları Açıklandı',
    date: '10 Ocak 2025',
    image: '/images/renders/render1.jpg',
    summary: 'Kooperatif üyelerimizle paylaşılan yeni yıl hedefleri doğrultusunda projenin teslim tarihlerinde öne çekilmeler planlanıyor.'
  }
];

const Home = () => {
  return (
    <div className="w-full overflow-hidden">
      
      {/* HERO SECTION SLIDER */}
      <section className="relative h-screen min-h-[700px]">
        <Swiper
          modules={[Navigation, Pagination, Autoplay, EffectFade]}
          effect="fade"
          speed={1000}
          autoplay={{ delay: 6000, disableOnInteraction: false }}
          pagination={{ clickable: true, dynamicBullets: true }}
          className="h-full w-full"
        >
          {heroSlides.map((slide) => (
            <SwiperSlide key={slide.id} className="relative h-full w-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/85 via-[#0f172a]/55 to-[#0f172a]/30 z-10" />
              <img 
                src={slide.image} 
                alt="İsilkoop Hero" 
                className="absolute inset-0 w-full h-full object-cover animate-[scale_20s_ease-in-out_infinite]"
                style={{ transformOrigin: 'center' }}
              />
              
              <div className="absolute inset-0 z-20 flex items-center justify-center pt-20">
                <div className="container mx-auto px-6 max-w-7xl">
                  <motion.div 
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false }}
                    variants={staggerContainer}
                    className="max-w-4xl"
                  >
                    <motion.span variants={fadeInUp} className="inline-block py-1 px-3 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30 text-sm font-semibold mb-6 backdrop-blur-md uppercase">
                      {slide.subtitle}
                    </motion.span>
                    <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-[1.1] mb-8 whitespace-pre-line">
                      {slide.title}
                    </motion.h1>
                    <motion.p variants={fadeInUp} className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl leading-relaxed">
                      {slide.desc}
                    </motion.p>
                    
                    <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-4 md:gap-6">
                      <Link to="/proje" className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all duration-300 shadow-[0_0_40px_rgba(37,99,235,0.3)] hover:shadow-[0_0_60px_rgba(37,99,235,0.5)] flex items-center gap-2 group text-lg">
                        Projeyi İncele
                        <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                      </Link>
                      
                      <a href="https://www.youtube.com/watch?v=Gsw55Ju4kiQ" target="_blank" rel="noreferrer" className="relative group overflow-hidden rounded-xl border-2 border-white/20 hover:border-white/50 transition-all duration-500 shadow-xl flex items-center">
                        <div className="absolute inset-0 bg-slate-900/40 group-hover:bg-transparent transition-colors z-10"></div>
                        <img src="https://img.youtube.com/vi/Gsw55Ju4kiQ/maxresdefault.jpg" alt="Tanıtım Filmi" className="w-48 h-16 object-cover group-hover:scale-110 transition-transform duration-700" />
                        <div className="absolute inset-0 z-20 flex items-center justify-center gap-2">
                          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform pl-1">
                            <Play size={18} className="fill-current" />
                          </div>
                          <span className="text-white font-bold tracking-wide drop-shadow-md">Tanıtım Filmi</span>
                        </div>
                      </a>
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* ABOUT PREVIEW SECTION */}
      <section className="py-24 md:py-32 bg-white relative">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              className="w-full lg:w-1/2"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-indigo-50 rounded-2xl transform rotate-2"></div>
                <img
                  src="/images/renders_hero/hero5.jpg"
                  alt="Kurumsal"
                  className="relative rounded-2xl shadow-2xl object-cover h-[500px] w-full"
                />
                <div className="absolute -bottom-8 -right-8 bg-white p-6 rounded-xl shadow-xl hidden md:block border border-slate-100">
                  <div className="text-4xl font-bold text-primary mb-1">2023</div>
                  <div className="text-slate-500 font-medium text-sm">Kuruluş Yılı</div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="w-full lg:w-1/2"
            >
              <motion.h4 variants={fadeInUp} className="text-blue-600 font-semibold uppercase tracking-wider text-sm mb-3">Hakkımızda</motion.h4>
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                Modern ve Kapsamlı <br/><span className="text-slate-500">Üretim Alanları</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-slate-600 mb-8 leading-relaxed">
                2023 yılında kurulan S.S. İkitelli - Silivri Toplu İşyeri Yapı Kooperatifi, bölgede iş dünyasının ihtiyaçlarına yönelik modern ve kapsamlı üretim-ticaret alanları oluşturmayı hedeflemiştir. Kooperatifimiz Silivri'de yer alan geniş bir arazi üzerinde proje geliştirme süreçlerine başlamıştır.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                    <Maximize size={24} />
                  </div>
                  <div>
                    <h5 className="font-semibold text-slate-900 mb-1">Geniş Arazi</h5>
                    <p className="text-sm text-slate-500">Silivri'de stratejik konumlu dev proje alanı.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
                    <Sparkles size={24} />
                  </div>
                  <div>
                    <h5 className="font-semibold text-slate-900 mb-1">Modern Altyapı</h5>
                    <p className="text-sm text-slate-500">Geleceğin üretim ihtiyaçlarına uygun.</p>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <Link to="/kurumsal/hakkimizda" className="inline-flex items-center gap-2 font-semibold text-primary hover:text-blue-600 transition-colors group">
                  <span className="border-b-2 border-primary group-hover:border-blue-600 pb-1">Devamını Oku</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ANNOUNCEMENTS / NEWS SLIDER SECTION */}
      <section className="py-24 bg-slate-50 border-t border-slate-100 overflow-hidden relative">
        <div className="container mx-auto px-6 max-w-7xl relative">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="flex flex-col md:flex-row md:items-end justify-between mb-12"
          >
            <div>
              <h4 className="text-blue-600 font-bold uppercase tracking-widest text-sm mb-3">Güncel</h4>
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Duyurular & Haberler</h2>
            </div>
            <div className="mt-6 md:mt-0 flex items-center gap-4">
              <div className="flex gap-2">
                <button className="swiper-button-prev-custom w-12 h-12 rounded-full border-2 border-slate-200 flex items-center justify-center text-slate-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all font-bold">
                  <ChevronLeft size={24} />
                </button>
                <button className="swiper-button-next-custom w-12 h-12 rounded-full border-2 border-slate-200 flex items-center justify-center text-slate-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all font-bold">
                  <ChevronRight size={24} />
                </button>
              </div>
              <div className="w-px h-10 bg-slate-200 hidden md:block mx-3"></div>
              <Link to="/haberler" className="text-slate-600 hover:text-blue-600 font-bold flex items-center gap-2 transition-colors text-lg">
                Tümünü Gör <ArrowRight size={20} />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="pb-12"
          >
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              navigation={{
                prevEl: '.swiper-button-prev-custom',
                nextEl: '.swiper-button-next-custom',
              }}
              pagination={{ clickable: true, dynamicBullets: true }}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="!pb-16"
            >
              {homeNewsItems.map((item) => (
                <SwiperSlide key={item.id} className="h-auto">
                  <div className="bg-white rounded-[2rem] overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-transform duration-300 group flex flex-col h-full">
                    <div className="h-64 overflow-hidden relative">
                      <div className="absolute inset-0 bg-blue-900/10 group-hover:bg-transparent transition-colors z-10"></div>
                      <img 
                        src={item.image} 
                        alt={item.title} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur px-4 py-2 rounded-xl text-xs font-bold text-slate-800 flex items-center gap-2 z-20 shadow-lg">
                        <Calendar size={16} className="text-blue-600" />
                        {item.date}
                      </div>
                    </div>
                    <div className="p-8 flex flex-col flex-1">
                      <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-600 transition-colors line-clamp-2 leading-tight">
                        {item.title}
                      </h3>
                      <p className="text-slate-600 text-base font-medium mb-6 line-clamp-3 flex-1 leading-relaxed">
                        {item.summary}
                      </p>
                      <Link to={`/haberler`} className="text-base font-bold text-blue-600 group-hover:text-blue-700 flex items-center gap-2 mt-auto">
                        Haberi İncele <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </section>

      {/* PARALLAX BANNER */}
      <section className="relative py-32 md:py-48 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed" 
          style={{ backgroundImage: "url('/images/renders/1.jpg')" }}
        ></div>
        <div className="absolute inset-0 bg-slate-900/60 mix-blend-multiply"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-6"
        >
          <span className="text-emerald-400 font-bold tracking-widest text-sm md:text-base uppercase mb-4 block">
            Geleceğin Vizyonu
          </span>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-tight drop-shadow-2xl">
            Trakya'nın En Büyük <br/> Karma Sanayi Sitesi
          </h2>
        </motion.div>
      </section>

      {/* COUNTER / RAKAMLARLA İSİLKOOP */}
      <section className="relative py-24 md:py-32 bg-gradient-to-b from-slate-50 via-white to-slate-50 overflow-hidden">
        {/* Decorative grid background */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(to right, #0f172a 1px, transparent 1px), linear-gradient(to bottom, #0f172a 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-50 pointer-events-none" />
        <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-emerald-100 rounded-full blur-3xl opacity-50 pointer-events-none" />

        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16 max-w-2xl mx-auto"
          >
            <h4 className="text-blue-600 font-semibold uppercase tracking-[0.2em] text-xs mb-4">
              Rakamlarla İsilkoop
            </h4>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Vizyonumuzu <span className="text-blue-600">Rakamlara</span> Döktük
            </h2>
            <p className="text-slate-500 mt-4 text-base md:text-lg">
              Bölgenin en büyük karma sanayi projesini şekillendiren değerler.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {counterStats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.15 }}
                  className="group relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white to-slate-50 rounded-3xl shadow-xl shadow-slate-200/60 group-hover:shadow-2xl group-hover:-translate-y-1 transition-all duration-500 border border-slate-100" />

                  <div className="relative p-8 md:p-10 text-center">
                    {/* Glow behind icon */}
                    <div className={`absolute top-6 left-1/2 -translate-x-1/2 w-24 h-24 ${stat.glow} blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                    {/* Icon */}
                    <div className={`relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.accent} text-white shadow-lg mb-6 group-hover:scale-110 transition-transform duration-500`}>
                      <Icon size={28} strokeWidth={2} />
                    </div>

                    {/* Number */}
                    <div className="flex items-baseline justify-center gap-1 mb-3">
                      <span className="text-5xl md:text-6xl font-extrabold bg-gradient-to-br from-slate-900 to-slate-700 bg-clip-text text-transparent tabular-nums tracking-tight">
                        <CountUp end={stat.value} />
                      </span>
                      {stat.suffix && (
                        <span className="text-xl md:text-2xl font-bold text-slate-400 ml-1">
                          {stat.suffix}
                        </span>
                      )}
                    </div>

                    {/* Label */}
                    <p className="text-sm md:text-base font-semibold text-slate-500 uppercase tracking-wider">
                      {stat.label}
                    </p>

                    {/* Bottom accent line */}
                    <div className={`mt-6 mx-auto h-1 w-12 rounded-full bg-gradient-to-r ${stat.accent} opacity-60 group-hover:w-20 transition-all duration-500`} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default Home;
